import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Mail, Sparkles, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import PasswordInput from "@/components/PasswordInput";
import DemoRequestModal from "@/components/DemoRequestModal";
import SiteFooter from "@/components/SiteFooter";
import { StoreButtons, useAppLinks } from "@/components/StoreButtons";
import { toast } from "sonner";

export default function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const appLinks = useAppLinks();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await signIn(email.trim().toLowerCase(), password.trim());
      toast.success("Welcome back");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" data-testid="admin-login-screen">
      <div className="flex-1 grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-slate-900 p-12 overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1757405944970-dfca42373629?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900/95 to-indigo-950/80" />
        <div className="relative flex items-center gap-3">
          <img src="/logo-lg.webp" alt="Smart-In" width="44" height="44" loading="eager" decoding="async"
            className="h-11 w-11 rounded-xl object-cover bg-white shadow-lg" />
          <span className="text-white font-head font-extrabold text-lg">Smart-In</span>
        </div>
        <div className="relative">
          <h2 className="font-head text-4xl font-extrabold text-white leading-tight tracking-tight">
            Workforce attendance,
            <br />
            verified by AI.
          </h2>
          <p className="mt-4 text-slate-300 text-base max-w-md leading-relaxed">
            Manage employees, geofenced sites, and AI face-match audits — all from one high-density
            command center.
          </p>
          <div className="mt-8 flex gap-8">
            {[
              ["Geofenced", "Sites"],
              ["Claude AI", "Face Match"],
              ["Real-time", "Attendance"],
            ].map(([a, b]) => (
              <div key={a}>
                <p className="text-white font-head font-bold text-lg">{a}</p>
                <p className="text-slate-400 text-sm">{b}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-slate-500 text-xs">
          © {new Date().getFullYear()} Smart-In AI Powered Attendance
          {" · "}
          <a href="https://smrtin.net/privacy" data-testid="login-brand-privacy-link" className="text-slate-400 hover:text-[#FFC400] font-semibold transition-colors">
            Privacy Policy
          </a>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <form onSubmit={submit} className="w-full max-w-md animate-fade-up">
          <Link to="/" data-testid="login-back-to-home-btn"
            className="inline-flex items-center gap-1.5 mb-6 h-9 px-3.5 rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:text-[#FF5A4D] hover:border-[#FF5A4D]/40 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/logo.webp" alt="Smart-In" width="44" height="44" loading="eager" decoding="async"
              className="h-11 w-11 rounded-xl object-cover bg-white shadow-md ring-1 ring-slate-200" />
            <span className="font-head font-extrabold text-lg text-slate-900">Smart-In</span>
          </div>

          <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900">Sign in</h1>
          <p className="mt-2 text-sm text-slate-500">Management &amp; Admin Portal — authorized staff only.</p>

          <div className="mt-8 space-y-5">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError("");
                  }}
                  placeholder="admin@company.com"
                  required
                  className="w-full h-12 rounded-xl border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Password</label>
              <PasswordInput
                testid="login-password-input"
                value={password}
                onChange={(v) => {
                  setPassword(v);
                  setError("");
                }}
                autoComplete="current-password"
                required
              />
            </div>

            {error && (
              <p className="text-sm font-medium text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </p>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 active:scale-[0.99] transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/20"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
            </button>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center"><span className="bg-slate-50 px-3 text-xs font-medium text-slate-400 uppercase tracking-wide">New here?</span></div>
            </div>

            <button
              type="button"
              data-testid="request-demo-btn"
              onClick={() => setShowDemo(true)}
              className="group w-full h-12 rounded-xl border border-indigo-200 bg-white text-indigo-700 font-semibold text-sm hover:bg-indigo-50 hover:border-indigo-300 active:scale-[0.99] transition-all flex items-center justify-center gap-2"
            >
              <Sparkles className="h-4 w-4 text-indigo-500 group-hover:scale-110 transition-transform" />
              Request a Demo Account
            </button>
            <p className="text-center text-xs text-slate-400">Full access for 14 days · unlimited sites &amp; staff</p>

            <div className="pt-2" data-testid="login-mobile-app-section">
              <p className="mb-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Staff? Get the mobile app</p>
              <StoreButtons links={appLinks} prefix="login" fluid />
            </div>
          </div>
        </form>
      </div>
      </div>

      <SiteFooter />

      <DemoRequestModal open={showDemo} onClose={() => setShowDemo(false)} />
    </div>
  );
}
